import { setOpacity } from "@memorang/helpers/setOpacity";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import type { LevelIconName } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { LevelIcon } from "../LevelIcon";

export type CountTone = "neutral" | "error" | "warning" | "success";

type AppTheme = ReturnType<typeof useAppTheme>;

type Props = {
	id: string;
	label: string;
	icon?: LevelIconName;
	iconColor?: string;
	countLabel?: string;
	countTone?: CountTone;
	countBackgroundColor?: string;
	countTextColor?: string;
	checked?: boolean;
	disabled?: boolean;
	/** Show a lock glyph instead of the checkbox (gated content). */
	locked?: boolean;
	selectionVariant?: "checkbox" | "radio";
	testID?: string;
	onToggle: (id: string) => void;
};

const getToneColors = ({
	colors,
	palette,
}: AppTheme): Record<CountTone, { bg: string; fg: string }> => {
	const fromConfidence = (fg: string) => ({
		fg,
		bg: setOpacity(fg, 0.15),
	});

	return {
		neutral: {
			bg: colors.surfaceVariant,
			fg: colors.onSurface,
		},
		error: fromConfidence(palette.confidence.low),
		warning: fromConfidence(palette.confidence.medium),
		success: fromConfidence(palette.confidence.high),
	};
};

const getControlIcon = (
	checked: boolean | undefined,
	selectionVariant: "checkbox" | "radio",
) => {
	if (selectionVariant === "radio") {
		return checked ? "radiobox-marked" : "radiobox-blank";
	}
	return checked ? "checkbox-marked" : "checkbox-blank-outline";
};

const OptionListItem = ({
	id,
	label,
	icon,
	iconColor,
	countLabel,
	countTone = "neutral",
	countBackgroundColor,
	countTextColor,
	checked,
	disabled,
	locked,
	selectionVariant = "checkbox",
	testID,
	onToggle,
}: Props) => {
	const theme = useAppTheme();

	const tone = getToneColors(theme)[countTone];
	const countBackground = countBackgroundColor ?? tone.bg;
	const countForeground = countTextColor ?? tone.fg;
	const optionIconColor =
		iconColor ?? (countTone === "neutral" ? theme.colors.onSurface : tone.fg);
	const controlIcon = getControlIcon(checked, selectionVariant);

	return (
		<Pressable
			onPress={() => onToggle(id)}
			disabled={disabled}
			accessibilityRole={selectionVariant === "radio" ? "radio" : "checkbox"}
			accessibilityState={{ checked, disabled }}
			style={{ opacity: disabled ? 0.5 : 1 }}
			testID={testID}
		>
			<Box flexDirection="row" alignItems="center" gap={12} paddingVertical={8}>
				{locked ? (
					<Icon
						name="lock-outline"
						size={24}
						color={theme.colors.onSurfaceVariant}
					/>
				) : (
					<Icon
						name={controlIcon}
						size={24}
						color={checked ? theme.colors.primary : theme.colors.outline}
					/>
				)}
				{icon && <LevelIcon name={icon} size={24} color={optionIconColor} />}
				<Text
					variant="bodyLarge"
					numberOfLines={2}
					style={{ flex: 1, color: theme.colors.onSurface }}
				>
					{label}
				</Text>
				{countLabel ? (
					<Box
						paddingHorizontal={8}
						paddingVertical={4}
						borderRadius={theme.roundness * 2}
						backgroundColor={countBackground}
					>
						<Text variant="bodySmall" style={{ color: countForeground }}>
							{countLabel}
						</Text>
					</Box>
				) : null}
			</Box>
		</Pressable>
	);
};

export default OptionListItem;
